import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

function fmt(n) {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function Fees() {
  const [perfPct, setPerfPct] = useState(5.0); // default mid value
  const [duration, setDuration] = useState(6); // default 6 months
  const [showSplit, setShowSplit] = useState(false);

  // Constants
  const VAULT_SIZE = 2_000_000;
  const INVESTOR = 100_000;
  const ENTRY_EXIT_FEE = 0.025;
  const PERF_FEE_RATE = 0.2; // stays at 30%
  const SPLIT_PARTNERS = 0.5;
  const SPLIT_MANAGER = 0.05;
  const SPLIT_QNC = 0.45;

  const { investor, vault } = useMemo(() => {
    const r = perfPct / 100;

    // Investor path
    const entryFeeInv = INVESTOR * ENTRY_EXIT_FEE;
    let invBalance = INVESTOR - entryFeeInv;

    // Vault path
    let nav = VAULT_SIZE;
    let vaultPerfFeeTotal = 0;

    for (let m = 1; m <= duration; m++) {
      // Investor compounding
      const grossInv = invBalance * r;
      const pfInv = grossInv * PERF_FEE_RATE;
      invBalance += grossInv - pfInv;

      // Vault compounding
      const grossVault = nav * r;
      const pfVault = grossVault * PERF_FEE_RATE;
      vaultPerfFeeTotal += pfVault;
      nav += grossVault - pfVault;
    }

    const exitFeeInv = invBalance * ENTRY_EXIT_FEE;
    const invFinal = invBalance - exitFeeInv;
    const invNetPct = ((invFinal - INVESTOR) / INVESTOR) * 100;

    const partners = vaultPerfFeeTotal * SPLIT_PARTNERS;
    const manager = vaultPerfFeeTotal * SPLIT_MANAGER;
    const qnc = vaultPerfFeeTotal * SPLIT_QNC;

    return {
      investor: {
        finalPayout: invFinal,
        netReturnPct: invNetPct,
      },
      vault: {
        perfFeeTotal: vaultPerfFeeTotal,
        partners,
        manager,
        qnc,
        finalNAV: nav,
      },
    };
  }, [perfPct, duration]);

  const durations = [
    { label: "3M", months: 3 },
    { label: "6M", months: 6 },
    { label: "12M", months: 12 },
    { label: "24M", months: 24 },
  ];

  return (
    <section id="fees" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-900"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Fee Structure
        </motion.h2>

        {/* Fee table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-md mb-12">
          <table className="w-full text-left">
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="p-6 font-semibold">Vault Setup Fee</td>
                <td className="p-6">$5,000 (one-time, per vault group)</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="p-6 font-semibold">Management Fee</td>
                <td className="p-6">1% annually (on AUM)</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="p-6 font-semibold">Performance Fee</td>
                <td className="p-6">
                  20% of monthly net profits (above high watermark)
                </td>
              </tr>
              <tr>
                <td className="p-6 font-semibold">Entry & Exit Fee</td>
                <td className="p-6">
                  2.5% on deposit and withdrawal (anti-arbitrage)
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Duration toggle */}
        <div className="flex justify-center gap-4 mb-10">
          {durations.map((d) => (
            <button
              key={d.months}
              onClick={() => setDuration(d.months)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                duration === d.months
                  ? "bg-cyan-600 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Performance slider */}
        <div className="mb-10">
          <label className="block text-center mb-4 text-lg font-medium text-gray-700">
            Average Monthly Performance:{" "}
            <span className="text-cyan-700">{perfPct.toFixed(2)}%</span>
          </label>
          <input
            type="range"
            min="0.1"
            max="28"
            step="0.01"
            value={perfPct}
            onChange={(e) => setPerfPct(Number(e.target.value))}
            className="w-full accent-cyan-600"
          />
          <p className="text-center text-sm text-gray-500 mt-2">
            Vault fixed at ${fmt(VAULT_SIZE)} • Investor fixed at ${fmt(INVESTOR)} • Compounded over {duration} months
          </p>
        </div>

        {/* Results */}
        <motion.div
          className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-xl font-semibold mb-8 text-gray-900 text-center">
            Example: ${fmt(VAULT_SIZE)} Vault • ${fmt(INVESTOR)} Investor • {perfPct.toFixed(2)}% avg / month • {duration} months
          </h3>

          {/* Vault Performance */}
          <div className="mb-10 text-center">
            <p className="text-3xl font-bold text-cyan-700">
              ${fmt(vault.finalNAV)}
            </p>
            <p className="text-gray-600 mt-2">Vault Final NAV</p>
          </div>

          {/* Investor Payout */}
          <div className="mb-10 text-center">
            <p className="text-3xl font-bold text-cyan-700">
              ${fmt(investor.finalPayout)}
            </p>
            <p className="text-gray-600 mt-2">Investor Final Payout</p>
            <p className="text-gray-500 text-sm mt-1">
              Net return: {investor.netReturnPct.toFixed(2)}%
            </p>
          </div>

          {/* Performance Fees Paid */}
          <div className="mb-6 text-center">
            <p className="text-3xl font-bold text-cyan-700">
              ${fmt(vault.perfFeeTotal)}
            </p>
            <p className="text-gray-600 mt-2">Performance Fees Paid</p>
            <button
              onClick={() => setShowSplit(!showSplit)}
              className="mt-3 text-sm text-cyan-700 underline hover:text-cyan-900"
            >
              {showSplit ? "Hide Split" : "Show Split"}
            </button>

            <AnimatePresence>
              {showSplit && (
                <motion.div
                  className="grid md:grid-cols-3 gap-8 text-center mt-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div>
                    <p className="text-2xl font-bold text-cyan-700">
                      ${fmt(vault.partners)}
                    </p>
                    <p className="text-gray-600 mt-2">Strategy Partners</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-cyan-700">
                      ${fmt(vault.manager)}
                    </p>
                    <p className="text-gray-600 mt-2">Group Manager</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-cyan-700">
                      ${fmt(vault.qnc)}
                    </p>
                    <p className="text-gray-600 mt-2">QuantNexus Capital</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <p className="mt-8 text-center text-gray-500 text-sm">
          Illustrative only. NAV growth compounds monthly after performance fees.
          Fees accrue automatically to QuantNexus, strategy partners, and group managers.
          Entry/exit fees (2.5%) apply separately to investor deposits/withdrawals.
        </p>
      </div>
    </section>
  );
}
